import Avatar from "@galaxy-io/dls/avatar/Avatar";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { TextVariant } from "@galaxy-io/dls/text/Text";
import type { Assignee } from "@gtm/contracts/core/assignments";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryState } from "@/components/query-state";
import { DrawerRow } from "@/components/workspace/drawer-content";
import { SelectField } from "@/components/workspace/select-field";
import { apiClient, unwrap } from "@/lib/api-client";

export function EntityAssignment({
  asValue = false,
  kind,
  id,
  assignedTo,
}: {
  kind: "person" | "company";
  id: string;
  assignedTo: Assignee | null;
  asValue?: boolean;
}) {
  const client = useQueryClient();
  const users = useQuery({
    queryKey: ["assignees"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/organization/assignees", { signal })),
  });
  const save = useMutation({
    mutationFn: (value: string) =>
      unwrap(
        apiClient().PATCH(kind === "person" ? "/people/{id}" : "/companies/{id}", {
          params: { path: { id } },
          body: { assignedUserId: value || null },
        }),
      ),
    onSuccess: async () => {
      await client.invalidateQueries();
    },
    onError: () => {
      void users.refetch();
    },
  });
  const options = [
    { value: "", label: "Unassigned", variant: TextVariant.TERTIARY },
    ...(users.data ?? []).map((user) => ({
      value: user.id,
      label: user.name,
      icon: <Avatar seed={user.name} size={16} />,
    })),
  ];
  if (assignedTo && !options.some((option) => option.value === assignedTo.id)) {
    options.push({
      value: assignedTo.id,
      label: assignedTo.status === "removed" ? "Former member" : assignedTo.name,
      icon: <Avatar seed={assignedTo.name} size={16} />,
    });
  }
  const content = (
    <>
      {users.error ? (
        <QueryState error={users.error} onRetry={() => void users.refetch()} />
      ) : users.isPending ? (
        <QueryState loadingMessage="Loading users…" />
      ) : (
        <SelectField
          hideLabel
          label="Assigned to"
          compact
          width={188}
          search
          value={assignedTo?.id ?? ""}
          options={options}
          disabled={save.isPending}
          onChange={(value) => {
            if (value !== (assignedTo?.id ?? "")) save.mutate(value);
          }}
        />
      )}
      {save.error && <Alert variant={AlertVariant.ERROR}>{save.error.message}</Alert>}
    </>
  );
  return asValue ? content : <DrawerRow label="Assigned to">{content}</DrawerRow>;
}
